import React, { useState, useCallback, useMemo, useEffect, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { skipToken } from "@reduxjs/toolkit/query";
import { Treemap, ResponsiveContainer } from "recharts";
import { ColorRing } from "react-loader-spinner";
import { Info, TrendingUp, TrendingDown, ChevronLeft } from "lucide-react";
import {
  useGetShapeSizeDistributionQuery,
  useGetColorClarityDistributionQuery,
} from "../../redux/slices/apiSlice";
import { selectDateRange } from "../../redux/slices/dateRangeSlice";
import { setIsPreFiltered, setPreDefinedFilter } from "../../redux/slices/pricingSlice";
import { transformFilters } from "../../utils/utils";

// API values can be { source, parsedValue } objects or plain numbers
function parseVal(v) {
  if (v == null) return 0;
  if (typeof v === "object" && v.parsedValue != null) return Number(v.parsedValue) || 0;
  return Number(v) || 0;
}

// Returns nested groups: [{ name: shape, children: [...tiles] }]
// Recharts Treemap keeps all children of each group spatially together.
// Each tile keeps the raw shapeKey ("0000round") + caratKey ("0.50-0.59")
// so a click can drill into the color/clarity distribution for that cell.
function processShapeSizeGrid(grid) {
  if (!grid || typeof grid !== "object") return [];
  const groups = [];
  for (const [shapeKey, shapeData] of Object.entries(grid)) {
    const shapeName = shapeData?.trans;
    if (!shapeName) continue;
    const children = [];
    for (const [key, bin] of Object.entries(shapeData)) {
      if (!bin || typeof bin !== "object") continue;
      if (!key.includes("-")) continue;
      const iv = parseVal(bin.inv_value);
      if (iv <= 0) continue;
      children.push({
        name:      `${shapeName} ${key}`,
        shape:     shapeName,
        shapeKey,
        caratKey:  key,
        size:      iv,
        inv_value: iv,
        turn:      parseVal(bin.turn),
        inventory: parseVal(bin.inventory),
      });
    }
    if (children.length === 0) continue;
    groups.push({ name: shapeName, children });
  }
  return groups;
}

// Color/clarity drill-down: outer keys are colors (trans "D"), inner keys are
// clarities (each bin has its own trans "VS2"). Same nested-group shape so the
// existing tile renderer works unchanged.
function processColorClarityGrid(grid) {
  if (!grid || typeof grid !== "object") return [];
  const groups = [];
  for (const [colorKey, colorData] of Object.entries(grid)) {
    const colorName = colorData?.trans;
    if (!colorName) continue;
    const children = [];
    for (const [key, bin] of Object.entries(colorData)) {
      if (key === "trans") continue;
      if (!bin || typeof bin !== "object") continue;
      const iv = parseVal(bin.inv_value);
      if (iv <= 0) continue;
      const clarityName = bin.trans || key;
      children.push({
        name:       `${colorName} ${clarityName}`,
        shape:      colorName,
        colorKey,        // raw color code (e.g. "0000d") for the PCP filter
        clarityKey: key, // raw clarity code (e.g. "0005vs2") for the PCP filter
        size:       iv,
        inv_value:  iv,
        turn:       parseVal(bin.turn),
        inventory:  parseVal(bin.inventory),
      });
    }
    if (children.length === 0) continue;
    groups.push({ name: colorName, children });
  }
  return groups;
}

// ─── Turn-rate colour tiers (5 tiers, thresholds relative to avgTurn) ───────────
// Colors shifted one step toward "good" since Very Poor is removed.
const TURN_TIERS = [
  { label: "Excellent",  color: "#10B981", bg: "#a7f3d0", textColor: "#047857" },
  { label: "Very Good",  color: "#84CC16", bg: "#f7fee7", textColor: "#3f6212" },
  { label: "Good",       color: "#EAB308", bg: "#fef9c3", textColor: "#713f12" },
  { label: "Fair",       color: "#F97316", bg: "#ffedd5", textColor: "#7c2d12" },
  { label: "Poor",       color: "#DC2626", bg: "#fee2e2", textColor: "#7f1d1d" },
];

// Thresholds: ≥1.5×avg → Excellent, ≥1.25×avg → Very Good, ≥avg → Good,
// ≥0.75×avg → Fair, else Poor.
function getTier(turn, avg) {
  const a = avg || 1;
  if (turn >= 1.5  * a) return TURN_TIERS[0];
  if (turn >= 1.25 * a) return TURN_TIERS[1];
  if (turn >= a)        return TURN_TIERS[2];
  if (turn >= 0.75 * a) return TURN_TIERS[3];
  return TURN_TIERS[4];
}

function fmtM(val) {
  if (!val) return "$0";
  if (val >= 1_000_000) return `$${(val / 1_000_000).toFixed(1)}M`;
  if (val >= 1_000)     return `$${(val / 1_000).toFixed(1)}K`;
  return `$${Math.round(val).toLocaleString()}`;
}

// Shared SVG text props — avoids repeating pointer-events + select on every element
const T = { style: { pointerEvents: "none", userSelect: "none" } };

// Centered spinner shown while a treemap level is loading
function TreemapLoader({ label }) {
  return (
    <div className="h-full flex flex-col items-center justify-center gap-2 border border-dashed border-gray-200 rounded-lg">
      <ColorRing
        visible={true}
        height="60"
        width="60"
        ariaLabel="blocks-loading"
        colors={["#00319f", "#2e56b1", "#5c7bc2", "#8aa1d3", "#b2c1e2"]}
      />
      {label && <span className="text-[12px] text-gray-400">{label}</span>}
    </div>
  );
}

// ─── SVG tile renderer factory ────────────────────────────────────────────────
function makeTileRenderer(onEnter, onLeave, onMove, avgTurn, onClick) {
  return function TileContent(props) {
    const { x, y, width, height, name, inv_value, turn, inventory, shape, depth, shapeKey, caratKey, colorKey, clarityKey } = props;

    if (!name || depth === 0 || width < 4 || height < 4) return null;

    // depth=1 → shape group boundary: thick white border + label watermark
    if (depth === 1) {
      return (
        <g style={{ pointerEvents: "none" }}>
          <rect x={x} y={y} width={width} height={height} fill="transparent" stroke="#fff" strokeWidth={4} rx={2} />
          {width > 50 && height > 18 && (
            <text
              x={x + width / 2} y={y + height / 2}
              textAnchor="middle" dominantBaseline="central"
              fill="rgba(255,255,255,0.18)" fontSize={Math.min(28, Math.floor(width / 4))}
              fontWeight="bold"
              style={{ pointerEvents: "none", userSelect: "none" }}
            >
              {name}
            </text>
          )}
        </g>
      );
    }

    // depth=2 → actual carat-range tile
    const tier    = getTier(turn || 0, avgTurn);
    const cx      = x + width  / 2;
    const cy      = y + height / 2;
    const carat   = shape ? name.slice(shape.length + 1) : name;
    const turnTxt = `Turn: ${Number(turn || 0).toFixed(1)}`;
    const valTxt  = fmtM(inv_value);

    const showFull = width > 130 && height > 68;
    const showMed4 = width > 55  && height > 56;
    const showMed2 = width > 42  && height > 30;
    const showTiny = width > 26  && height > 16;

    // Shape/carat tiles drill down; drilled color/clarity tiles jump to Pricing Co-Pilot.
    const clickable = !!onClick && ((!!shapeKey && !!caratKey) || (!!colorKey && !!clarityKey));

    return (
      <g
        onMouseEnter={(e) => onEnter(e, { name, inv_value, turn, inventory })}
        onMouseLeave={onLeave}
        onMouseMove={onMove}
        onClick={clickable ? () => onClick({ name, shapeKey, caratKey, colorKey, clarityKey }) : undefined}
        style={{ cursor: clickable ? "pointer" : "default" }}
      >
        <rect x={x} y={y} width={width} height={height} fill={tier.color} stroke="#fff" strokeWidth={1.5} />

        {showFull && (
          <>
            <text x={cx} y={cy - 13} textAnchor="middle" dominantBaseline="central" fill="#fff" fontWeight="bold" fontSize={13} {...T}>{name}</text>
            <text x={cx} y={cy +  3} textAnchor="middle" dominantBaseline="central" fill="rgba(255,255,255,0.9)"  fontSize={11} {...T}>{valTxt}</text>
            <text x={cx} y={cy + 17} textAnchor="middle" dominantBaseline="central" fill="rgba(255,255,255,0.75)" fontSize={10} {...T}>{turnTxt}</text>
          </>
        )}

        {!showFull && showMed4 && (
          <>
            <text x={cx} y={cy - 18} textAnchor="middle" dominantBaseline="central" fill="#fff" fontWeight="bold" fontSize={11} {...T}>{shape}</text>
            <text x={cx} y={cy -  6} textAnchor="middle" dominantBaseline="central" fill="rgba(255,255,255,0.9)"  fontSize={10} {...T}>{carat}</text>
            <text x={cx} y={cy +  6} textAnchor="middle" dominantBaseline="central" fill="rgba(255,255,255,0.85)" fontSize={10} {...T}>{valTxt}</text>
            <text x={cx} y={cy + 18} textAnchor="middle" dominantBaseline="central" fill="rgba(255,255,255,0.75)" fontSize={9}  {...T}>{turnTxt}</text>
          </>
        )}

        {!showFull && !showMed4 && showMed2 && (
          <>
            <text x={cx} y={cy - 6} textAnchor="middle" dominantBaseline="central" fill="#fff" fontWeight="bold" fontSize={Math.min(11, Math.floor(width / 5.5))} {...T}>{shape}</text>
            <text x={cx} y={cy + 6} textAnchor="middle" dominantBaseline="central" fill="rgba(255,255,255,0.85)" fontSize={Math.min(10, Math.floor(width / 6.5))} {...T}>{carat}</text>
          </>
        )}

        {!showFull && !showMed4 && !showMed2 && showTiny && (
          <text x={cx} y={cy} textAnchor="middle" dominantBaseline="central" fill="#fff" fontWeight="bold" fontSize={Math.min(10, Math.floor(width / 4))} {...T}>{shape}</text>
        )}
      </g>
    );
  };
}

// ─── Main component ────────────────────────────────────────────────────────────
export default function InventoryTreemap({ accountId, invValue, avgTurn }) {
  const [tooltip, setTooltip] = useState(null);
  const dispatch = useDispatch();
  const navigate = useNavigate();

  // Same endpoint/date-range the BIAnalysis shape-carat grid uses.
  const dateRange = useSelector(selectDateRange);
  const { data: apiData, isFetching } = useGetShapeSizeDistributionQuery(
    {
      id: accountId,
      startDate: dateRange.startDate,
      endDate: dateRange.endDate,
      filters: {},
    },
    { skip: !accountId },
  );
  const shapeSizeGrid = apiData?.data;

  // ── Drill-down: clicking a shape/carat tile loads its color×clarity breakdown
  const [drill, setDrill] = useState(null); // { shapeKey, caratKey, label } | null

  const handleTileClick = useCallback((item) => {
    // Top level: a shape/carat tile drills into its color × clarity breakdown.
    if (item?.shapeKey && item?.caratKey) {
      setTooltip(null);
      setDrill({ shapeKey: item.shapeKey, caratKey: item.caratKey, label: item.name });
      return;
    }

    // Drilled level: a color/clarity tile jumps to Pricing Co-Pilot pre-filtered by
    // the drilled shape + carat range and this tile's color + clarity.
    if (item?.colorKey && item?.clarityKey && drill) {
      setTooltip(null);
      const shapeValue =
        drill.shapeKey === "0007cushion_brilliant"
          ? [drill.shapeKey, "0008cushion_modified"]
          : [drill.shapeKey];
      const filterState = {
        discrete: {
          "diamond#shape": shapeValue,
          "gem#carats": [
            { from: drill.caratKey.split("-")[0], to: drill.caratKey.split("-")[1] },
          ],
          "diamond#color": [item.colorKey],
          "diamond#clarity": [item.clarityKey],
        },
      };
      dispatch(setIsPreFiltered(true));
      dispatch(setPreDefinedFilter(transformFilters(filterState)));
      navigate(`/price_co-pilot?page=0`);
    }
  }, [drill, dispatch, navigate]);
  const exitDrill = useCallback(() => {
    setTooltip(null);
    setDrill(null);
  }, []);

  const { data: ccData, isFetching: ccFetching } = useGetColorClarityDistributionQuery(
    drill
      ? {
          id: accountId,
          startDate: dateRange.startDate,
          endDate: dateRange.endDate,
          shape: drill.shapeKey,
          caratRange: drill.caratKey,
          filters: {},
        }
      : skipToken,
    { refetchOnMountOrArgChange: true },
  );

  // Entrance animation — mount the Treemap only when it scrolls into view so
  // recharts plays its tile-grow animation at the right moment.
  const treemapRef = useRef(null);
  const [inView, setInView] = useState(false);

  const handleEnter = useCallback((e, item) => {
    setTooltip({ x: e.clientX, y: e.clientY, ...item });
  }, []);
  const handleLeave = useCallback(() => setTooltip(null), []);
  // onMove only updates position — avoids re-allocating the item object every pixel
  const handleMove = useCallback((e) => {
    setTooltip(prev => prev ? { ...prev, x: e.clientX, y: e.clientY } : null);
  }, []);

  const tileRenderer = useMemo(
    () => makeTileRenderer(handleEnter, handleLeave, handleMove, avgTurn, handleTileClick),
    [handleEnter, handleLeave, handleMove, avgTurn, handleTileClick],
  );

  const groups   = useMemo(() => processShapeSizeGrid(shapeSizeGrid), [shapeSizeGrid]);
  const ccGroups = useMemo(() => processColorClarityGrid(ccData?.data), [ccData]);

  const isDrill        = !!drill;
  const activeGroups   = isDrill ? ccGroups : groups;
  const activeFetching = isDrill ? ccFetching : isFetching;
  const tiles  = useMemo(() => activeGroups.flatMap(g => g.children || []), [activeGroups]);

  const avg    = avgTurn || 0;
  const high   = useMemo(() => tiles.filter(d => d.turn >= 1.25 * avg).length, [tiles, avg]);
  const medium = useMemo(() => tiles.filter(d => d.turn >= avg && d.turn < 1.25 * avg).length, [tiles, avg]);
  const low    = useMemo(() => tiles.filter(d => d.turn < avg).length, [tiles, avg]);

  // The whole panel shows once the top-level data exists; inside, we swap the
  // treemap data between shape/carat and the drilled color/clarity view.
  const hasData       = groups.length > 0;
  const activeHasData = activeGroups.length > 0;

  // Attach the observer once the treemap div is actually in the DOM. The div
  // only renders when hasData is true, so re-run when that flips.
  useEffect(() => {
    const el = treemapRef.current;
    if (!el || inView) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setInView(true);
          observer.disconnect();
        }
      },
      { threshold: 0.1 },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [hasData, inView]);

  return (
    <div className="bg-white rounded-xl border border-gray-200 shadow-sm px-5 py-4">
      {/* ── Header ── */}
      <div className="flex items-start justify-between pb-3 border-b border-gray-100 mb-4">
        <div>
          {isDrill ? (
            <>
              <div 
                onClick={exitDrill}
                className="flex items-center gap-1 text-[12px] font-medium text-blue-600 hover:text-blue-700 mb-1 border w-max px-2 py-1 rounded-md border-blue-600 hover:cursor-pointer"
              >
                <ChevronLeft className="w-3.5 h-3.5" />
                Inventory Breakdown
              </div>
              <h2 className="text-[17px] font-semibold text-gray-900">
                {drill.label} · Color &amp; Clarity
              </h2>
              <p className="text-[12px] text-gray-500 mt-0.5">
                Size represents inventory value, color indicates turnover rate · click a tile to open it in Pricing Co-Pilot
              </p>
            </>
          ) : (
            <>
              <h2 className="text-[17px] font-semibold text-gray-900">
                Inventory Breakdown by Category
              </h2>
              <p className="text-[12px] text-gray-500 mt-0.5">
                Size represents inventory value, color indicates turnover rate · click a tile to drill into color &amp; clarity
              </p>
            </>
          )}
        </div>
        {invValue != null && (
          <div className="text-right flex-shrink-0">
            <p className="text-[11px] text-gray-400 font-medium">Total Inventory</p>
            <p className="text-[24px] font-extrabold text-gray-900 leading-none mt-0.5">
              {fmtM(invValue)}
            </p>
            <p className="text-[12px] text-gray-500 mt-1">
              Avg Turn:{" "}
              <span className="font-semibold text-gray-700">
                {avgTurn != null ? Number(avgTurn).toFixed(2) : "–"}
              </span>
            </p>
          </div>
        )}
      </div>

      {/* ── Empty / loading state ── */}
      {!hasData && (
        <div className="h-[460px]">
          {isFetching ? (
            <TreemapLoader label="Loading inventory data…" />
          ) : (
            <div className="h-full flex items-center justify-center text-gray-300 text-xs border border-dashed border-gray-200 rounded-lg">
              No inventory data available
            </div>
          )}
        </div>
      )}

      {/* ── Treemap + legend + summary cards ── */}
      {hasData && (
        <>
          {/* Container keeps its height before inView so layout doesn't jump;
              fade + slide-up entrance fires once when scrolled into view. */}
          {/* CSS transform on this div would trap position:fixed children —
              so the tooltip is rendered OUTSIDE this div, after it. */}
          <div
            ref={treemapRef}
            style={{
              height: 460,
              opacity: inView ? 1 : 0,
              transform: inView ? "none" : "translateY(20px)",
              transition: "opacity 0.55s ease-out, transform 0.55s ease-out",
            }}
          >
            {/* While a drill fetch is in flight, show loading — never the stale
                previous result that RTK Query still holds during the transition. */}
            {inView && activeFetching && (
              <TreemapLoader label={isDrill ? "Loading color & clarity…" : "Loading inventory data…"} />
            )}
            {inView && !activeFetching && !activeHasData && (
              <div className="h-full flex items-center justify-center text-gray-300 text-xs border border-dashed border-gray-200 rounded-lg">
                {isDrill ? "No color & clarity data for this category" : "No inventory data available"}
              </div>
            )}
            {inView && !activeFetching && activeHasData && (
              <ResponsiveContainer width="100%" height="100%">
                <Treemap
                  key={isDrill ? `cc-${drill.shapeKey}-${drill.caratKey}` : "shape-size"}
                  data={activeGroups}
                  dataKey="size"
                  aspectRatio={4 / 3}
                  content={tileRenderer}
                  isAnimationActive
                  animationDuration={500}
                />
              </ResponsiveContainer>
            )}
          </div>

          {/* Tooltip lives outside the animated div so position:fixed is always
              relative to the real viewport, not the transformed ancestor. */}
          {tooltip && inView && (() => {
            const TW  = 220; // safe upper-bound tooltip width
            const TH  = 135; // approximate tooltip height
            const GAP = 12;
            // visualViewport reflects the actual visible area at any zoom level
            const vw  = window.visualViewport?.width  ?? window.innerWidth;
            const vh  = window.visualViewport?.height ?? window.innerHeight;
            const left = tooltip.x + GAP + TW <= vw
              ? tooltip.x + GAP
              : Math.max(GAP, tooltip.x - TW - GAP);
            const top = Math.max(GAP, Math.min(vh - TH - GAP, tooltip.y - TH / 2));
            return (
              <div
                style={{ position: "fixed", left, top, zIndex: 9999, pointerEvents: "none" }}
                className="bg-white border border-gray-200 rounded-xl shadow-xl px-3 py-2.5 min-w-[190px]"
              >
                <p className="font-bold text-gray-900 text-[13px] mb-2">{tooltip.name}</p>
                <div className="flex items-center justify-between gap-4 mb-1">
                  <span className="text-[12px] text-gray-500">Inventory Value:</span>
                  <span className="text-[12px] font-bold text-gray-800">{fmtM(tooltip.inv_value)}</span>
                </div>
                <div className="flex items-center justify-between gap-4 mb-1">
                  <span className="text-[12px] text-gray-500">Turnover (12M):</span>
                  <span className="text-[12px] font-bold text-gray-800">
                    {Number(tooltip.turn || 0).toFixed(2)}
                  </span>
                </div>
                <div className="flex items-center justify-between gap-4">
                  <span className="text-[12px] text-gray-500">Performance:</span>
                  {(() => {
                    const tier = getTier(tooltip.turn || 0, avgTurn);
                    return (
                      <span
                        className="text-[11px] font-bold px-2 py-0.5 rounded-md"
                        style={{ backgroundColor: tier.bg, color: tier.textColor }}
                      >
                        {tier.label}
                      </span>
                    );
                  })()}
                </div>
              </div>
            );
          })()}

          {/* ── Legend ── */}
          <div className="mt-4 pt-3 border-t border-gray-100">
            <div className="flex items-center gap-1.5 mb-2">
              <Info className="w-3.5 h-3.5 text-gray-400" />
              <span className="text-[12px] font-semibold text-gray-500">Turnover Rate Legend</span>
            </div>
            <div className="flex flex-wrap gap-x-5 gap-y-1.5">
              {(() => {
                const a = avgTurn || 0;
                const suffixes = [
                  `≥${(1.5  * a).toFixed(2)}`,
                  `≥${(1.25 * a).toFixed(2)}`,
                  `≥${a.toFixed(2)}`,
                  `≥${(0.75 * a).toFixed(2)}`,
                  `<${(0.75 * a).toFixed(2)}`,
                ];
                return TURN_TIERS.map((tier, i) => (
                  <div key={tier.label} className="flex items-center gap-1.5">
                    <span className="w-3 h-3 rounded-sm flex-shrink-0" style={{ backgroundColor: tier.color }} />
                    <span className="text-[12px] font-semibold text-gray-700">{tier.label}</span>
                    <span className="text-[11px] text-gray-400">{suffixes[i]}</span>
                  </div>
                ));
              })()}
            </div>
          </div>

          {/* ── Summary cards ── */}
          <div className="grid grid-cols-3 gap-3 mt-4">
            <div className="rounded-xl border border-emerald-100 bg-emerald-50 px-4 py-3">
              <div className="flex items-center gap-1.5 mb-1">
                <TrendingUp className="w-3.5 h-3.5 text-emerald-500 flex-shrink-0" />
                <span className="text-[12px] font-bold text-emerald-700">High Performers</span>
              </div>
              <p className="text-[11px] text-emerald-600 font-medium">
                {high} {high === 1 ? "category" : "categories"} — Excellent &amp; Very Good
              </p>
            </div>
            <div className="rounded-xl border border-amber-100 bg-amber-50 px-4 py-3">
              <div className="flex items-center gap-1.5 mb-1">
                <Info className="w-3.5 h-3.5 text-amber-500 flex-shrink-0" />
                <span className="text-[12px] font-bold text-amber-700">Medium Performers</span>
              </div>
              <p className="text-[11px] text-amber-600 font-medium">
                {medium} {medium === 1 ? "category" : "categories"} — Good (at Avg Turn)
              </p>
            </div>
            <div className="rounded-xl border border-red-100 bg-red-50 px-4 py-3">
              <div className="flex items-center gap-1.5 mb-1">
                <TrendingDown className="w-3.5 h-3.5 text-red-500 flex-shrink-0" />
                <span className="text-[12px] font-bold text-red-700">Need Attention</span>
              </div>
              <p className="text-[11px] text-red-600 font-medium">
                {low} {low === 1 ? "category" : "categories"} — Fair &amp; Poor (below Avg Turn)
              </p>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
